import * as React from "react";
import { useTranslation } from "react-i18next";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import ProviderIcon from "@/components/AICatalog/ProviderIcon";
import { SectionCard } from "./SectionCard";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/utils/cn";
import { formatCompactNumber, formatCurrency, formatNumber } from "../utils";
import { useTopProviders, type MetricScope, type StatsFilters } from "../api";

type Props = { filters: StatsFilters; metricScope: MetricScope };

const COLORS = ["#a8e6c3", "#ccf9e0", "#86c9a6", "#e4fbee", "#5fa888"];
const SIZE_OPTIONS = [5, 10, 25, 50];

export function ProviderUsage({ filters, metricScope: selectedScope }: Props) {
  const { t } = useTranslation();
  const [size, setSize] = React.useState(5);
  const {
    data: response,
    isLoading,
    metricScope,
  } = useTopProviders(filters, size, selectedScope);

  const showSpend = metricScope !== "usage";
  const rows = (response?.data ?? []).map((row) => ({
    provider: row.provider ?? "unknown",
    requests: row.total_requests,
    spend: row.total_spend ?? 0,
    successful: row.successful_requests,
    failed: row.failed_requests,
    tokens: row.total_tokens,
  }));

  const metricValue = (row: (typeof rows)[number]) =>
    showSpend ? row.spend : row.requests;
  const chartData = rows.filter((row) => metricValue(row) > 0);
  const total = rows.reduce((sum, row) => sum + metricValue(row), 0);
  const pieData =
    total > 0
      ? chartData
      : [
          {
            provider: "",
            requests: 1,
            spend: 1,
            successful: 0,
            failed: 0,
            tokens: 0,
          },
        ];

  return (
    <SectionCard
      title={t("usageAndCost.providerUsage.title")}
      category={t("usageAndCost.categories.all")}
      headerExtra={
        <div
          role="tablist"
          aria-label={t("usageAndCost.providerUsage.sizeAriaLabel")}
          className="inline-flex max-w-full shrink-0 items-center gap-0.5 overflow-x-auto rounded-xl bg-secondary p-1"
        >
          {SIZE_OPTIONS.map((s) => (
            <button
              key={s}
              type="button"
              role="tab"
              aria-selected={size === s}
              onClick={() => setSize(s)}
              className={cn(
                "shrink-0 rounded-lg px-2 py-0.5 text-xs font-medium transition-colors",
                size === s
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {s}
            </button>
          ))}
        </div>
      }
      bodyClassName="p-0"
    >
      <div className="grid grid-cols-1 items-center lg:grid-cols-[280px_1fr]">
        <div className="relative mx-auto h-[220px] w-[220px]">
          {isLoading ? (
            <Skeleton className="h-full w-full rounded-full" />
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey={showSpend ? "spend" : "requests"}
                    nameKey="provider"
                    innerRadius="65%"
                    outerRadius="95%"
                    paddingAngle={0}
                    stroke="none"
                    isAnimationActive={false}
                  >
                    {pieData.map((_, i) => (
                      <Cell
                        key={i}
                        fill={
                          total > 0 ? COLORS[i % COLORS.length] : "var(--muted)"
                        }
                      />
                    ))}
                  </Pie>
                  {total > 0 && (
                    <Tooltip
                      formatter={(value) =>
                        showSpend
                          ? formatCurrency(Number(value))
                          : formatNumber(Number(value))
                      }
                    />
                  )}
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
                <span className="text-xs text-muted-foreground">
                  {t(
                    showSpend
                      ? "usageAndCost.stats.totalSpend"
                      : "usageAndCost.stats.totalRequests",
                  )}
                </span>
                <span
                  className="text-lg font-semibold text-foreground"
                  aria-label={t(
                    showSpend
                      ? "usageAndCost.stats.totalSpend"
                      : "usageAndCost.stats.totalRequests",
                  )}
                >
                  {showSpend ? formatCurrency(total) : formatNumber(total)}
                </span>
              </div>
            </>
          )}
        </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-muted-foreground">
              <th className="px-5 py-3 text-start text-xs font-medium">
                {t("usageAndCost.providerUsage.provider")}
              </th>
              {showSpend && (
                <th className="px-5 py-3 text-end text-xs font-medium">
                  {t("usageAndCost.providerUsage.spendUsd")}
                </th>
              )}
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.successful")}
              </th>
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.failed")}
              </th>
              <th className="px-5 py-3 text-end text-xs font-medium">
                {t("usageAndCost.providerUsage.tokens")}
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: Math.min(size, 5) }).map((_, i) => (
                <tr key={i} className="border-b border-border last:border-b-0">
                  {Array.from({ length: showSpend ? 5 : 4 }).map((__, j) => (
                    <td key={j} className="px-5 py-3">
                      <Skeleton className="h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td
                  colSpan={showSpend ? 5 : 4}
                  className="px-5 py-10 text-center text-sm text-muted-foreground"
                >
                  {t("usageAndCost.providerUsage.noData")}
                </td>
              </tr>
            ) : (
              rows.map((row, idx) => (
                <tr
                  key={`${row.provider}-${idx}`}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-5 py-3">
                    <span className="inline-flex items-center gap-2 text-foreground">
                      <ProviderIcon provider={row.provider} />
                      {row.provider}
                    </span>
                  </td>
                  {showSpend && (
                    <td className="px-5 py-3 text-end text-foreground">
                      {formatCurrency(row.spend)}
                    </td>
                  )}
                  <td className="px-5 py-3 text-end text-emerald-400">
                    {row.successful}
                  </td>
                  <td className="px-5 py-3 text-end text-destructive">
                    {row.failed}
                  </td>
                  <td className="px-5 py-3 text-end text-muted-foreground">
                    {formatCompactNumber(row.tokens)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      </div>
    </SectionCard>
  );
}
